<template>
  <div class="notifications">
    <!-- Errors interrupt (assertive); confirmations wait their turn (polite). -->
    <div class="notifications__list" role="region" aria-label="Уведомления">
      <div
          v-for="item in messages"
          :key="item.id"
          class="toast fade show notifications__toast"
          :class="`notifications__toast--${item.variant.name}`"
          role="alert"
          :aria-live="item.variant.live"
          aria-atomic="true"
          @mouseenter="pauseDismiss(item)"
          @mouseleave="resumeDismiss(item)"
      >
        <div class="toast-header notifications__header">
          <i :class="item.variant.icon" class="notifications__icon" aria-hidden="true"></i>
          <strong class="mr-auto">{{ item.variant.title }}</strong>
          <button
              @click="removeMessage(item.id)"
              type="button"
              class="close notifications__close"
              :aria-label="`Закрыть уведомление: ${item.variant.title}`"
          >
            <i class="bi-x" aria-hidden="true"></i>
          </button>
        </div>
        <div class="toast-body notifications__body">
          {{ item.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Errors stay long enough to be read and copied; confirmations get out of the
// way. Previously nothing dismissed at all and toasts stacked up indefinitely.
const DISMISS_MS = {
  alert: 9000,
  warning: 7000,
  success: 4500,
  info: 4500
};

const VARIANTS = {
  alert: { name: "alert", title: "Ошибка", icon: "bi-exclamation-octagon-fill", live: "assertive" },
  warning: { name: "warning", title: "Предупреждение", icon: "bi-exclamation-triangle-fill", live: "assertive" },
  success: { name: "success", title: "Готово", icon: "bi-check-circle-fill", live: "polite" },
  info: { name: "info", title: "Сообщение", icon: "bi-info-circle-fill", live: "polite" }
};

// Beyond this the stack covers the screen and buries the newest message.
const MAX_VISIBLE = 4;

let nextId = 0;

export default {
  name: "Notifications",
  data() {
    return {
      messages: []
    };
  },
  mounted() {
    this.notificationsToken = this.$notifications.subscribe((message) => this.addMessage(message));
  },
  unmounted() {
    this.$notifications.unsubscribe(this.notificationsToken);
    this.messages.forEach((item) => clearTimeout(item.timer));
  },
  methods: {
    addMessage({ type, message }) {
      const variant = VARIANTS[type] || VARIANTS.alert;

      // Collapse repeats: a retry loop firing the same error five times should
      // not produce five identical toasts.
      const duplicate = this.messages.find(
          (item) => item.message === message && item.variant.name === variant.name
      );
      if (duplicate) {
        this.scheduleDismiss(duplicate);
        return;
      }

      const item = {
        id: ++nextId,
        message,
        variant,
        timer: null
      };
      this.messages.push(item);

      while (this.messages.length > MAX_VISIBLE) {
        const dropped = this.messages.shift();
        clearTimeout(dropped.timer);
      }

      this.scheduleDismiss(item);
    },
    scheduleDismiss(item) {
      clearTimeout(item.timer);
      item.timer = setTimeout(() => this.removeMessage(item.id), DISMISS_MS[item.variant.name]);
    },
    /** Don't yank a message away while the user is reading or selecting it. */
    pauseDismiss(item) {
      clearTimeout(item.timer);
    },
    resumeDismiss(item) {
      this.scheduleDismiss(item);
    },
    removeMessage(id) {
      const index = this.messages.findIndex((item) => item.id === id);
      if (index === -1) {
        return;
      }
      clearTimeout(this.messages[index].timer);
      this.messages.splice(index, 1);
    }
  }
};
</script>

<style lang="scss">
.notifications {
  position: relative;

  &__list {
    position: fixed;
    bottom: 0;
    right: 0;
    // Must sit above Bootstrap's modal (1050) and our processing loader —
    // without a z-index, an error raised from inside a modal was invisible.
    z-index: var(--z-toast);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding: 0 1rem 1rem 0;
    // Let clicks through the empty area of the container, but not the toasts.
    pointer-events: none;
  }

  // Written as descendant selectors rather than `&__toast`, which would compile
  // to a single class and tie Bootstrap's own `.toast { border: 1px solid ... }`
  // on specificity — losing the accent border to source order.
  .notifications__toast {
    pointer-events: auto;
    // Bootstrap's `.toast` ships `flex-basis: 350px` alongside its
    // `max-width: 350px`, which is written for a row. This list is a COLUMN
    // flex container, so the main axis is vertical and that 350px landed on
    // the toast's HEIGHT: a two-line error rendered as a 350px slab with a
    // quarter-screen of empty white under the text. Overriding `width` alone
    // did not touch it, because the height was never a width problem.
    flex: 0 0 auto;
    // Was a fixed 20rem, which overflowed viewports narrower than ~320px.
    width: auto;
    max-width: min(22rem, calc(100vw - 2rem));
    margin-top: 0.5rem;
    border: none;
    border-left: 0.25rem solid var(--text-secondary);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    animation: notifications-enter var(--duration-slow) var(--ease-out);
  }

  .notifications__toast--alert {
    border-left-color: var(--color-error-base);
    .notifications__icon { color: var(--color-error-base); }
  }

  .notifications__toast--warning {
    border-left-color: var(--color-warning-base);
    .notifications__icon { color: var(--color-warning-base); }
  }

  .notifications__toast--success {
    border-left-color: var(--success-color);
    .notifications__icon { color: var(--success-color); }
  }

  .notifications__toast--info {
    border-left-color: var(--primary-blue-color);
    .notifications__icon { color: var(--primary-blue-color); }
  }

  // Descendant selector for the same reason: Bootstrap's `.toast-header` sets
  // its own background and would otherwise win on source order.
  .notifications__header {
    background: none;
    border-bottom: 1px solid var(--border-subtle);
    color: var(--text-primary);
    font-weight: 600;
  }

  &__icon {
    margin-right: 0.5rem;
    font-size: 1rem;
    line-height: 1;
  }

  &__close {
    // 44px minimum touch target — the old 'bi-x small' icon was ~12px.
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.75rem;
    min-height: 1.75rem;
    margin-left: 0.5rem;
    color: var(--text-secondary);
    opacity: 1;
    transition: color var(--duration-fast) var(--ease-out);

    &:hover {
      color: var(--text-primary);
    }
  }

  .notifications__body {
    color: var(--text-primary);
    font-size: 1rem;
    text-wrap: pretty;
  }
}

@keyframes notifications-enter {
  from {
    opacity: 0;
    transform: translateY(0.75rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
